import { Controller } from "@hotwired/stimulus"

const RECENTS_KEY = "workplace.search.recents"
const MAX_RECENTS = 5
const MIN_QUERY_LENGTH = 1
const SEARCH_DELAY = 200

export default class extends Controller {
  static targets = ["input", "panel", "overlay", "clear", "recentsTemplate", "recentItemTemplate"]
  static values = { suggestionsUrl: String }

  connect() {
    this.searchTimer = null
    this.abortController = null
    this.highlightIndex = -1
    this._handleOutsideClick = this._handleOutsideClick.bind(this)
    this._handleDocumentKeydown = this._handleDocumentKeydown.bind(this)
    document.addEventListener("click", this._handleOutsideClick)
    document.addEventListener("keydown", this._handleDocumentKeydown)
    this.element.classList.add("workplace-search--connected")
    this._syncClear()
  }

  disconnect() {
    document.removeEventListener("click", this._handleOutsideClick)
    document.removeEventListener("keydown", this._handleDocumentKeydown)
    this._cancelSearch()
  }

  onFocus() {
    if (this.inputTarget.value.trim().length === 0) {
      this._showRecents()
    } else if (this.inputTarget.value.trim().length >= MIN_QUERY_LENGTH) {
      this._scheduleSearch()
    }
  }

  onInput() {
    this._syncClear()
    const query = this.inputTarget.value.trim()
    if (query.length === 0) {
      this._showRecents()
      return
    }
    if (query.length < MIN_QUERY_LENGTH) {
      this._hidePanel()
      return
    }
    this._scheduleSearch()
  }

  onKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
        if (!this._panelOpen()) return
        event.preventDefault()
        this._moveHighlight(1)
        break
      case "ArrowUp":
        if (!this._panelOpen()) return
        event.preventDefault()
        this._moveHighlight(-1)
        break
      case "Enter":
        if (this._panelOpen() && this.highlightIndex >= 0) {
          event.preventDefault()
          this._options()[this.highlightIndex]?.click()
        }
        break
      case "Escape":
        this._hidePanel()
        break
    }
  }

  _handleDocumentKeydown(event) {
    if (event.key === "Escape") this._hidePanel()
  }

  clear(event) {
    event.preventDefault()
    this.inputTarget.value = ""
    this._syncClear()
    this.inputTarget.focus()
    this._showRecents()
  }

  remember() {
    this._storeRecent(this.inputTarget.value)
  }

  runRecent(event) {
    const query = event.currentTarget.dataset.query
    if (!query) return

    this.inputTarget.value = query
    this._storeRecent(query)
    this.inputTarget.form.requestSubmit()
  }

  onOverlayClick() {
    this._hidePanel()
  }

  _handleOutsideClick(event) {
    if (!this._panelOpen()) return
    if (this.element.contains(event.target)) return

    this._hidePanel()
  }

  _showRecents() {
    const recents = this._recents()
    if (recents.length === 0) {
      this._hidePanel()
      return
    }

    const fragment = this.recentsTemplateTarget.content.cloneNode(true)
    const list = fragment.querySelector("[data-recents-list]")
    recents.forEach((query) => {
      const item = this.recentItemTemplateTarget.content.firstElementChild.cloneNode(true)
      item.textContent = query
      item.dataset.query = query
      list.appendChild(item)
    })

    this.panelTarget.replaceChildren(fragment)
    this._openPanel({ wide: false })
  }

  _scheduleSearch() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => this._runSearch(), SEARCH_DELAY)
  }

  async _runSearch() {
    const query = this.inputTarget.value.trim()
    if (query.length < MIN_QUERY_LENGTH) return

    this._cancelInFlight()
    this.abortController = new AbortController()

    try {
      const url = new URL(this.suggestionsUrlValue, window.location.origin)
      url.searchParams.set("q", query)
      const response = await fetch(url, {
        headers: { Accept: "text/html" },
        credentials: "same-origin",
        signal: this.abortController.signal
      })
      if (!response.ok) return

      this.panelTarget.innerHTML = await response.text()
      if (this.panelTarget.childElementCount === 0) {
        this._hidePanel()
        return
      }

      this._openPanel({ wide: Boolean(this.panelTarget.querySelector(".hub-search-segment")) })
    } catch (error) {
      if (error?.name === "AbortError") return
    }
  }

  _openPanel({ wide = false } = {}) {
    this.panelTarget.hidden = false
    this.panelTarget.classList.toggle("is-wide", wide)
    this.overlayTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
    this.highlightIndex = -1
    this._updateHighlighted()
  }

  _hidePanel() {
    this._cancelSearch()
    this.panelTarget.hidden = true
    this.panelTarget.replaceChildren()
    this.panelTarget.classList.remove("is-wide")
    this.overlayTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.highlightIndex = -1
  }

  _panelOpen() {
    return !this.panelTarget.hidden
  }

  _options() {
    return Array.from(this.panelTarget.querySelectorAll(".hub-search-option"))
  }

  _moveHighlight(offset) {
    const options = this._options()
    if (options.length === 0) return

    this.highlightIndex = (this.highlightIndex + offset + options.length) % options.length
    this._updateHighlighted()
    options[this.highlightIndex].scrollIntoView({ block: "nearest" })
  }

  _updateHighlighted() {
    const options = this._options()
    options.forEach((option, index) => {
      const active = index === this.highlightIndex
      option.classList.toggle("is-active", active)
      if (active) {
        this.inputTarget.setAttribute("aria-activedescendant", option.id || "")
      }
    })
    if (this.highlightIndex < 0) this.inputTarget.removeAttribute("aria-activedescendant")
  }

  _syncClear() {
    this.clearTarget.hidden = this.inputTarget.value.length === 0
  }

  _recents() {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]")
      return Array.isArray(stored) ? stored.filter((query) => typeof query === "string" && query.trim()) : []
    } catch {
      return []
    }
  }

  _storeRecent(rawQuery) {
    const query = rawQuery.toString().trim()
    if (query.length === 0) return

    const recents = [query, ...this._recents().filter((item) => item.toLowerCase() !== query.toLowerCase())]
      .slice(0, MAX_RECENTS)
    localStorage.setItem(RECENTS_KEY, JSON.stringify(recents))
  }

  _cancelSearch() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
    this.searchTimer = null
    this._cancelInFlight()
  }

  _cancelInFlight() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }
}
